'use client';

import { useState } from 'react';

export interface CheckoutItem {
  article_id: string;
  quantity: number;
}

interface CheckoutState {
  isPending: boolean;
  error: string | null;
}

interface CheckoutOptions {
  shopId: string;
  currency: string;
  customerEmail?: string;
}

interface CheckoutResult {
  checkout_url?: string;
  order_id?: string;
}

/**
 * Hook that initiates a Stripe checkout session for one or more items.
 * Matches the Meteor /stripe-checkout flow: redirect to Stripe hosted checkout.
 */
export function useCheckout(options: CheckoutOptions) {
  const [state, setState] = useState<CheckoutState>({
    isPending: false,
    error: null,
  });

  async function startCheckout(
    items: CheckoutItem | CheckoutItem[],
  ): Promise<CheckoutResult> {
    setState({ isPending: true, error: null });

    const itemsArray = Array.isArray(items) ? items : [items];

    try {
      const res = await fetch('/api/shops/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_id: options.shopId,
          items: itemsArray,
          customer_email: options.customerEmail ?? '',
          currency: options.currency,
        }),
      });

      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        // `error` is an i18n key (e.g. 'shop:checkoutFailed') resolved by the
        // presentation layer; fall back to the generic checkout-failed key.
        const message = data.error ?? 'shop:checkoutFailed';
        setState({ isPending: false, error: message });
        return {};
      }

      const data = (await res.json()) as CheckoutResult;
      setState({ isPending: false, error: null });
      return data;
    } catch {
      setState({ isPending: false, error: 'shop:networkError' });
      return {};
    }
  }

  function clearError() {
    setState((prev) => ({ ...prev, error: null }));
  }

  return {
    isPending: state.isPending,
    error: state.error,
    startCheckout,
    clearError,
  };
}
